import { Fragment, useState, useEffect, useCallback, useRef } from 'react'
import { UserIcon, GiftIcon } from '@repo/ui'
import { useGetCurrentLanguage, useGetGameConfig, useGetCurrentGameId } from 'src/store/game'
import { useFreeBetCount, useHeldRewardsCount } from 'src/features/inventory/store'
import { gameSupportsFreeBets } from 'src/features/inventory/utils/gameSupportsFreeBets'
import { isMarbleGame } from 'src/types/domain/Game'
import type { MarbleGame } from 'src/types/domain/Game'
import CogIcon from 'src/app/assets/icons/ico-cog.svg'
import ClipboardListCheckIcon from 'src/app/assets/icons/ico-clipboard-list-check.svg'
import HistoryIcon from 'src/app/assets/icons/ico-history.svg'
import TimesIcon from 'src/app/assets/icons/ico-times.svg'
import WalletIcon from 'src/app/assets/icons/ico-wallet.svg'
import CocktailIcon from 'src/app/assets/icons/ico-cocktail.svg'
import ExternalLinkIcon from 'src/app/assets/icons/ico-external-link.svg'
import FlagCheckeredIcon from 'src/app/assets/icons/ico-flag-checkered.svg'
import { isAuthenticatedPlayer } from 'src/types/domain/Player'
import type { Player } from 'src/types/domain/Player'
import { useClientSideTranslation } from 'src/services/i18n'
import { transformPresets, useViewportScale } from 'src/hooks/useViewportScale'
import { useSoundEffect } from 'src/hooks/useSoundEffect'
import { useGetCurrentPlayerDisplayName } from 'src/store/player'
import { BetHistoryPanelContainer } from '../BetHistoryPanel/BetHistoryPanelContainer'
import { ClearButton } from '../ClearButton'
import { ExternalLink } from '../ExternalLink'
import { PlayerPanel } from './components/PlayerPanel'
import { SettingsPanel } from './components/SettingsPanel'
import { PastRacesPanel } from './components/PastRacesPanel'
import { RulesPanel } from './components/RulesPanel'
import { RewardsSidebarPanel } from './components/RewardsSidebarPanel'

type Props = Readonly<{
  activePanel: string
  setActivePanel: (panel: string) => void
  isOpen: boolean
  onClose: () => void
  player: Player
  showStatsAndRules: boolean
  game?: MarbleGame
}>

export default function RightSidebar({
  activePanel,
  setActivePanel,
  isOpen,
  onClose,
  player,
  showStatsAndRules,
  game
}: Props) {
  const { t } = useClientSideTranslation(useGetCurrentLanguage())
  const { externalLobbyUrl, externalDepositUrl } = useGetGameConfig()
  const currentDisplayName = useGetCurrentPlayerDisplayName()
  const currentGameId = useGetCurrentGameId()
  const heldRewardsCount = useHeldRewardsCount(currentGameId)
  const freeBetCount = useFreeBetCount(currentGameId)
  // v12.4 ask 6: the badge must not count free bets on crash games — the
  // rewards panel hides them there (backend hard-rejects wagering them), so
  // counting them would advertise rewards the panel then doesn't show.
  const supportsFreeBets = gameSupportsFreeBets(
    game !== undefined && isMarbleGame(game) ? game.track.gameType : undefined
  )
  const rewardsCount = supportsFreeBets ? heldRewardsCount : heldRewardsCount - freeBetCount
  // Rewards / bonus features do not apply to crash — hide the Rewards tab there.
  const isCrashGame = game !== undefined && isMarbleGame(game) && game.track.gameType === 'crash'
  const sidebarRef = useRef<HTMLDivElement>(null)
  const { playEffect } = useSoundEffect()

  const [isAnimating, setIsAnimating] = useState(false)
  const [isVisible, setIsVisible] = useState(false)
  const scaleStyle = useViewportScale({
    transform: transformPresets.basic
  })

  const handleClickOutside = useCallback(
    (event: MouseEvent) => {
      // Check if the click was on the toolbar button
      const toolbarButton = document.querySelector('.toolbar-button')
      const isToolbarButtonClick = toolbarButton?.contains(event.target as Node)

      // The crash provably-fair modal is PORTALED to <body>, so its clicks
      // are "outside" the sidebar DOM — without this guard, tapping
      // anywhere inside the modal (opened from the bet-history pane)
      // closed the sidebar and unmounted the modal mid-read (v47).
      const target = event.target as Element | null
      const isFairnessModalClick = Boolean(
        target && typeof target.closest === 'function' && target.closest('.crash-round-modal')
      )

      if (
        sidebarRef.current &&
        !sidebarRef.current.contains(event.target as Node) &&
        isOpen &&
        !isToolbarButtonClick && // Ignore clicks on the toolbar button
        !isFairnessModalClick
      ) {
        onClose()
      }
    },
    [isOpen, onClose]
  )

  useEffect(() => {
    // Only add the listener if the sidebar is open
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen, handleClickOutside])

  useEffect(() => {
    if (isOpen) {
      setIsVisible(true)
      // Slight delay to ensure visibility is set before animation starts
      setTimeout(() => {
        setIsAnimating(true)
      }, 10)
    } else {
      setIsAnimating(false)
      // Wait for animation to finish before hiding
      setTimeout(() => {
        setIsVisible(false)
      }, 300)
    }
  }, [isOpen])

  const handleBackClick = () => {
    playEffect('click')
    setActivePanel('none')
  }

  const panels = {
    ...(isAuthenticatedPlayer(player)
      ? {
          player: {
            label: t('Player'),
            icon: UserIcon,
            content: null // Content is shown in default view
          }
        }
      : {}),
    settings: {
      label: t('Settings'),
      icon: CogIcon,
      content: <SettingsPanel onBackClick={handleBackClick} />
    },
    ...(rewardsCount > 0 && !isCrashGame
      ? {
          rewards: {
            label: t('Rewards'),
            icon: GiftIcon,
            content: <RewardsSidebarPanel onBackClick={handleBackClick} />,
            badge: rewardsCount
          }
        }
      : {}),
    ...(showStatsAndRules
      ? {
          betHistory: {
            label: t('Bet History'),
            icon: HistoryIcon,
            content: isAuthenticatedPlayer(player) ? (
              <BetHistoryPanelContainer onBackClick={handleBackClick} />
            ) : null
          },
          pastRaces: {
            label: t('Past Games'),
            icon: FlagCheckeredIcon,
            content: <PastRacesPanel onBackClick={handleBackClick} />
          },
          ...(game && {
            rules: {
              label: t('Rules'),
              icon: ClipboardListCheckIcon,
              content: <RulesPanel game={game} onBackClick={handleBackClick} />
            }
          })
        }
      : {})
  }

  const handlePanelClick = (panelKey: string) => {
    if (panelKey === 'none' && activePanel === 'none') {
      return
    }

    // Special handling for player panel - always navigate to default view
    if (panelKey === 'player') {
      setActivePanel('none')
      return
    }

    setActivePanel(panelKey !== activePanel ? panelKey : 'none')
  }

  const hasActivePanel = activePanel !== 'none'

  if (!isVisible) return null

  const activePanelClass = hasActivePanel ? 'panel-active' : ''
  const isAnimatingClass = isAnimating ? 'open' : ''

  return (
    <div
      className={`marbles-right-sidebar visible ${isAnimatingClass} ${activePanelClass}`}
      ref={sidebarRef}
      style={scaleStyle}
    >
      <div className="marbles-right-sidebar__inner">
        <div className="marbles-right-sidebar__content no-scrollbar">
          <div className="marbles-right-sidebar__player">
            <div className="player-name">
              <div className="avatar">
                <UserIcon />
              </div>
              <div className="name">
                <span className="label">{t('Player')}</span>
                <span className="value">{currentDisplayName ?? t('Anonymous')}</span>
              </div>
            </div>

            <div className="external_actions">
              {externalLobbyUrl ? (
                <ExternalLink href={externalLobbyUrl} label={t('Back to Home')}>
                  <span className="icon">
                    <CocktailIcon />
                  </span>
                  <span className="link-icon">
                    <ExternalLinkIcon />
                  </span>
                </ExternalLink>
              ) : null}

              {externalDepositUrl ? (
                <ExternalLink href={externalDepositUrl} label={t('Deposit')}>
                  <span className="icon">
                    <WalletIcon />
                  </span>
                  <span className="link-icon">
                    <ExternalLinkIcon />
                  </span>
                </ExternalLink>
              ) : null}
            </div>
          </div>
          {hasActivePanel ? panels[activePanel as keyof typeof panels]?.content : null}
          {!hasActivePanel
            ? isAuthenticatedPlayer(player) && (
                <div className="player-panel-reveal">
                  <PlayerPanel />
                </div>
              )
            : null}
        </div>
        <div className={`marbles-right-sidebar__nav ${activePanelClass}`}>
          <button className="close" onClick={onClose} type="button">
            <span className="inner">
              <span className="icon">
                <TimesIcon />
              </span>
            </span>
          </button>
          {Object.entries(panels).map(([key, panel]) => {
            const Icon = panel.icon
            const badge = 'badge' in panel ? panel.badge : undefined
            // For player panel, highlight when activePanel is 'none' (default view)
            const isActive = key === 'player' ? activePanel === 'none' : activePanel === key

            return (
              <Fragment key={key}>
              <ClearButton
                className={`${isActive ? 'active' : ''} panel-${key}`}
                onClick={() => {
                  handlePanelClick(key)
                }}
              >
                <span className="inner" style={{ position: 'relative', overflow: 'visible' }}>
                  <span className="icon">
                    <Icon />
                  </span>
                  {badge ? (
                    <span
                      style={{
                        position: 'absolute',
                        top: -3,
                        right: -3,
                        minWidth: 15,
                        height: 15,
                        padding: '0 4px',
                        borderRadius: 999,
                        background: '#2bd17e',
                        color: '#06281a',
                        fontSize: 9,
                        fontWeight: 700,
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        lineHeight: 1,
                        boxShadow: '0 0 0 2px rgba(16,20,28,0.9)'
                      }}
                    >
                      {badge}
                    </span>
                  ) : null}
                </span>
              </ClearButton>
              </Fragment>
            )
          })}
        </div>
      </div>
    </div>
  )
}
